import { formatDistance as formatDistanceFns, formatISO as formatISOFns, parseISO } from 'date-fns';
import { format as formatFns, toZonedTime } from 'date-fns-tz';
import { isSameDay, isValid } from './checkers';
import { ptBR } from './locale';
import { endOfDay, startOfDay, subDays } from './operations';

/**
 * @param {Date | number | string} date
 * @param {string} dateFormat
 * @returns {string}
 */
const format = (date: Date | number | string, dateFormat = 'dd/MM/yyyy', tz = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  try {
    const dateInstance = typeof date === 'string' ? parseISO(date) : date;

    return formatFns(toZonedTime(dateInstance, tz), dateFormat, {
      timeZone: tz,
      locale: ptBR,
    });
  } catch (error) {
    console.error(error);
    return 'Invalid date';
  }
};

/**
 * @param {Date | number | string} date
 * @param {Date | number | string} baseDate
 * @param {Boolean | undefined} options.addSuffix
 * @returns {string}
 */
const formatDistance = (date: Date | number | string, baseDate: Date | number | string, { addSuffix }: { addSuffix?: boolean } = {}) => {
  if (isValid(date) && isValid(baseDate)) {
    const dateInstance = typeof date === 'string' ? parseISO(date) : date;
    const baseDateInstance = typeof baseDate === 'string' ? parseISO(baseDate) : baseDate;

    return formatDistanceFns(dateInstance, baseDateInstance, { locale: ptBR, addSuffix });
  }

  return 'Invalid date';
};

/**
 * @param {Date | number | string} date
 * @param {'start' | 'end'} [format]
 * @returns {string}
 */
const formatISO = (date: Date | number | string, format?: 'start' | 'end') => {
  const dateInstance = typeof date === 'string' ? parseISO(date) : date;

  if (format === 'start') return formatISOFns(startOfDay(dateInstance));
  if (format === 'end') return formatISOFns(endOfDay(dateInstance));

  return formatISOFns(dateInstance);
};

/**
 * @param {number} now - The current timestamp in milliseconds.
 * @param {number} lastStatus - The last status timestamp in milliseconds.
 * @param {string} [dateTimeSeparator] - The separator between the date and time.
 * @returns {string}
 */
const formatYesterdayOrToday = (now: number, lastStatus: number, dateTimeSeparator = 'às') => {
  const beginOfLastStatusDay = startOfDay(lastStatus).getTime();
  const beginOfToday = startOfDay(now).getTime();
  const beginOfYesterday = startOfDay(subDays(new Date(), 1)).getTime();

  if (isSameDay(beginOfLastStatusDay, beginOfToday)) {
    return `Hoje ${dateTimeSeparator} ${format(lastStatus, 'HH:mm')}`;
  }

  if (isSameDay(beginOfLastStatusDay, beginOfYesterday)) {
    return `Ontem ${dateTimeSeparator} ${format(lastStatus, 'HH:mm')}`;
  }

  return `${format(lastStatus, 'dd/MM/yyyy')} ${dateTimeSeparator} ${format(lastStatus, 'HH:mm')}`;
};

/**
 * @param {number} durationInMilliseconds
 * @param {boolean} [showSeconds]
 * @returns {string}
 */
const formatDuration = (durationInMilliseconds: number, showSeconds?: boolean) => {
  const hours = Math.floor(durationInMilliseconds / 3600000);
  const minutes = Math.floor((durationInMilliseconds % 3600000) / 60000);
  const seconds = Math.floor((durationInMilliseconds % 60000) / 1000);

  const hoursStr = hours > 0 ? `${hours}h ` : '';
  const minutesStr = minutes > 0 ? `${minutes}min ` : '';
  const secondsStr = seconds > 0 && showSeconds ? `${seconds}s ` : '';

  if (hours || minutes || seconds) return `${hoursStr}${minutesStr}${secondsStr}`.trim();

  return showSeconds ? '0s' : '0min';
};

export { format, formatISO, formatDistance, formatYesterdayOrToday, formatDuration };
